'use client';
import PageTitle from '@/entities/PageTitle/ui/PageTitle';
import { AdvicesSlider } from '@/features/advices-slider';
import { CatalogCategoriesSlider } from '@/features/catalog-categories-slider';
import { CatalogCategoryFilters } from '@/features/catalog-category-filters';
import { CatalogCategorySorts } from '@/features/catalog-category-sorts';
import { ICatalogCard } from '@/shared/api/getCatalogCards/types';
import { ICatalogFilter } from '@/shared/api/getCatalogFilterState/types';
import { ICatalogProducts } from '@/shared/api/getCatalogProducts/types';
import { IPopularSection, IProduct } from '@/shared/api/getMainPage/types';
import { useQueryClient } from '@tanstack/react-query';

import Pagination from '@/features/pagination/ui/Pagination';
import { postCatalogFilterApply } from '@/shared/api/postCatalogFilterApply';
import { useCityStore } from '@/shared/store/cityStore';
import { useFiltersStore } from '@/shared/store/filtersStore';
import { Container } from '@/shared/ui/Container';
import LoadingSpinner from '@/shared/ui/LoadingSpinner/LoadingSpinner';
import ShowMoreButton from '@/shared/ui/ShowMoreButton/ShowMoreButton';
import TogglerSimple from '@/shared/ui/TogglerSimple/TogglerSimple';
import { formatCurrency } from '@/shared/utils/formatters';
import { twMergeExt } from '@/shared/utils/twMergeExt';
import { wordEnding } from '@/shared/utils/wordEnding';
import { CatalogCategoryAboutSection } from '@/widgets/catalog-category-about-section';
import PageHeader from '@/widgets/PageHeader/ui/PageHeader';
import { ProductPreviewCard } from '@/widgets/product-preview-card';
import { useQuery } from '@tanstack/react-query';
import { cva } from 'class-variance-authority';
import { Form, Formik } from 'formik';
import { usePathname } from 'next/navigation';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

interface Props {
  className?: string;
  popularSections: IPopularSection[];
  initialData: ICatalogProducts;
  category?: string;
  sectionCode?: string;
  filters: ICatalogFilter[];
  sectionId?: number;
  searchQuery?: string;
  recommendProducts: IProduct[] | [];
  initialLabel?: number | null;
  cards: ICatalogCard[] | [];
}

export const CatalogCategorySection: FC<Props> = ({
  className,
  popularSections,
  initialData,
  category,
  sectionCode,
  filters,
  sectionId,
  searchQuery,
  recommendProducts,
  initialLabel,
  cards,
}) => {
  const queryClient = useQueryClient();
  const pathname = usePathname();
  // const [
  //   filtersRangeStateDebounced,
  //   setFiltersRangeStateDebounced,
  // ] = useState<any>(null);
  // const [
  //   filtersStateDebounced,
  //   setFiltersStateDebounced,
  // ] = useState<any>(null);
  const setFilter = useFiltersStore((state) => state.setFilter);
  const setPage = useFiltersStore((state) => state.setPage);

  const setSort = useFiltersStore((state) => state.setSort);
  const setShopId = useFiltersStore((state) => state.setShopId);
  const setQuery = useFiltersStore((state) => state.setQuery);

  const pathnameArr = pathname.split('/');

  const page = parseInt(
    !isNaN(Number(pathnameArr[pathnameArr.length - 2])) ? pathnameArr[pathnameArr.length - 2] : '1',
    10
  );
  const filtersSortByState = useFiltersStore((state) => state.sort_by);
  const filtersSortOrderState = useFiltersStore((state) => state.sort_order);

  const getFilterStateBody = useFiltersStore((state) => state.getFilterBody);

  const resetFilters = useFiltersStore((state) => state.resetFilters);

  const formInitialValues = {
    expressDelivery: false,
    NAZNACHENIE: [],
    shop_ids: [],
    sort:
      getFilterStateBody().sort_by === undefined
        ? 'popular'
        : getFilterStateBody().sort_order === 'desc'
          ? '-' + getFilterStateBody().sort_by
          : getFilterStateBody().sort_by,
  };

  const city = useCityStore((state) => state.city);
  const initialCityRef = useRef(city);

  const [filterStateForQuery, setFilterStateForQuery] = useState<any>(getFilterStateBody());

  const {
    data: paginatedData,
    isFetching: isLoading,
    refetch,
  } = useQuery({
    queryKey: [
      'catalog',
      page,
      initialData.page_data.page,
      sectionCode,
      filtersSortByState,
      filtersSortOrderState,
      searchQuery,
      getFilterStateBody(),
      filterStateForQuery,
    ],

    queryFn: () => {
      return postCatalogFilterApply({
        ...getFilterStateBody(),
        // filter: {
        //   SECTION: [sectionId as number],
        // },
        query: searchQuery ?? '',
      });
    },
    // enabled: false,
    // initialData:
    //   initialData &&
    //   page === initialData.page_data.page
    //     ? initialData
    //     : undefined,
    // staleTime: 0,
    initialData: initialData,
    // Устанавливаю дату обновления initialData заведомо очень старой,
    // иначе запросы с клиента перестают исполнятся на время, указанное в staleTime.
    // staleTime: 0 тоже решает проблему, но по сути отключает кэширование.
    initialDataUpdatedAt: 0,
  });

  useEffect(() => {
    return () => {
      queryClient.removeQueries({
        queryKey: ['catalog'],
      });
    };
  }, [pathname]);

  useEffect(() => {
    if (city !== initialCityRef.current) {
      refetch();
    }
  }, [city, refetch]);
  // useEffect(() => {
  //   setTimeout(() => {
  //     refetch();
  //   }, 3000);
  // }, []);
  const getShopIds = useFiltersStore((state) => state.getShopIds);

  const totalCount = paginatedData?.page_data.total_items || 0;

  // const [] = useDebounce(
  //   async () => {
  //     setFiltersStateDebounced(filtersState);
  //   },
  //   300,
  //   [filtersState]
  // );

  // const [] = useDebounce(
  //   async () => {
  //     setFiltersRangeStateDebounced(
  //       filtersRangeState
  //     );
  //   },
  //   300,
  //   [filtersRangeState]
  // );

  const [showMoreTogglers, setShowMoreTogglers] = useState(false);

  const [showNaznachenieItems, setShowNaznachenieItems] = useState(false);

  const handleCheckboxGroupChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked, name } = e.target;
    setFilter(name, value, checked);
    setFilterStateForQuery(getFilterStateBody());
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const isDescending = value.startsWith('-');
    const sortField = isDescending ? value.substring(1) : value;
    const sortOrder = isDescending ? 'desc' : 'asc';
    setSort(sortField, sortOrder);
  };

  const resetShopIds = useFiltersStore((state) => state.resetShopIds);

  const handleShopChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;

    if (getShopIds().length > 15) {
      resetShopIds();
    }

    setShopId(Number(value), checked);
    setFilterStateForQuery(getFilterStateBody());
  };

  const manualShopChange = (value: string | number, checked: boolean) => {
    setShopId(Number(value), checked);
  };

  const mainRef = useRef<HTMLDivElement>(null);

  const handleSubmit = async () => {
    try {
      //
    } catch (e) {
      // console.error(e);
    }
  };

  useEffect(() => {
    if (getFilterStateBody().filter.SECTION && getFilterStateBody().filter.SECTION[0] !== sectionId) {
      console.log(getFilterStateBody().filter.SECTION[0], 'getFilterStateBody().filter.SECTION[0]');
      console.log(sectionId, 'sectionId');

      resetFilters();
    }

    console.log(initialLabel, 'initialLabel');

    if (initialLabel) {
      setFilter('LABEL', `${initialLabel}`, true);
      setFilter('SECTION', null, false);
    } else if (sectionId) {
      setFilter('SECTION', `${sectionId}`, true);
      setFilter('LABEL', '', false);
    }

    if (searchQuery) {
      setQuery(searchQuery);
    }
    setPage(page);
  }, [sectionId]);

  const onPageClick = (page: number) => {
    if (window) {
      window.scrollTo(100, 0);
    }
    setPage(page);
    if (window) {
      window.scrollTo(100, 0);
    }
  };

  const buildPageHref = (page: number) => {
    const queryParams = searchQuery ? `?q=${searchQuery}` : '';

    const pathnameArr = pathname.split('/');
    const length = pathnameArr.length;
    const pageIndex = pathnameArr[length - 2];
    const isPageIndexNumber = !isNaN(Number(pageIndex));
    if (!isPageIndexNumber) {
      if (page === 1) {
        return `${pathname}${queryParams}`;
      }

      return `${pathname}${page}/${queryParams}`;
    }

    if (page === 1) {
      const pathnameWithoutPageNumber = pathnameArr.slice(0, -2).join('/') + '/';
      return `${pathnameWithoutPageNumber}${queryParams}`;
    }

    return `${pathname.replace(`${pageIndex}/`, `${page}/`)}${queryParams}`;
  };

  const isShowAdvicesSlider = useMemo(() => cards?.length && cards?.length > 0, [cards?.length]);

  return (
    <div className={twMergeExt(cvaRoot(), className, isShowAdvicesSlider ? 'overflow-x-hidden' : '')}>
      <Container className={'flex items-start gap-0.8'} variant={'small'}>
        <h1 className={'font-secondary text-h1 text-cBlack'}>
          {category ? category : searchQuery ? 'Результаты поиска' : 'Каталог'}
        </h1>
        {totalCount ? (
          <div className={cvaTotalCount()}>
            {`${formatCurrency(totalCount, true)} ${wordEnding(totalCount, ['товар', 'товара', 'товаров'])}`}
          </div>
        ) : null}
      </Container>

      <CatalogCategoriesSlider className={'mt-2.4 lg:mt-4.8'} items={popularSections} />
      <div className={cvaFiltersWrapper()}>
        <div
          className={cvaFilters({
            fixed: false,
          })}
        >
          {!searchQuery ? (
            <Container variant={'small'} className="border-y-[1px] border-cBlack16 sm:border-none">
              <Formik initialValues={formInitialValues} enableReinitialize onSubmit={handleSubmit}>
                {({ getFieldProps }) => (
                  <>
                    <Form autoComplete="off">
                      <div className={cvaFiltersRow()}>
                        <CatalogCategoryFilters
                          manualShopChange={manualShopChange}
                          fieldProps={getFieldProps}
                          onOpenNaznachenie={(value) => setShowNaznachenieItems(value)}
                          filters={filters}
                          isLoading={isLoading}
                          onShopChange={handleShopChange}
                          initialSectionId={sectionId}
                          initialLabel={initialLabel}
                          setFilterStateForQuery={setFilterStateForQuery}
                        />
                        <CatalogCategorySorts fieldProps={getFieldProps} onChange={handleSortChange} />
                      </div>
                      {filters.find((item) => item.code === 'NAZNACHENIE')?.values?.length && showNaznachenieItems ? (
                        <div className={cvaTogglersWrapper()}>
                          <div className={cvaTogglers()}>
                            {filters
                              .find((item) => item.code === 'NAZNACHENIE')
                              ?.values?.map((item, idx) => {
                                return idx < 12 || showMoreTogglers ? (
                                  <TogglerSimple
                                    onChange={handleCheckboxGroupChange}
                                    key={item.value}
                                    name={'NAZNACHENIE'}
                                    value={Number(item.value)}
                                    title={item.code}
                                  />
                                ) : null;
                              })}
                            {filters.find((item) => item.code === 'NAZNACHENIE')?.values?.length &&
                            filters.find((item) => item.code === 'NAZNACHENIE')?.values?.length > 12 ? (
                              <ShowMoreButton
                                isActive={showMoreTogglers}
                                count={
                                  (filters.find((item) => item.code === 'NAZNACHENIE')?.values?.length || 0) - 12 ||
                                  0 - 12
                                }
                                text={'Ещё'}
                                onClick={() => setShowMoreTogglers((prev) => !prev)}
                              />
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                    </Form>
                  </>
                )}
              </Formik>
            </Container>
          ) : null}
        </div>
      </div>
      <Container variant={'large'}>
        <div
          className={cvaProductsList({
            isLoading: isLoading,
          })}
        >
          {paginatedData?.products && !isLoading
            ? paginatedData.products.map((i: IProduct, index: number) => (
                <React.Fragment key={i.id}>
                  {index === 3 && isShowAdvicesSlider ? <AdvicesSlider key={cards[0].id} cards={cards} /> : null}
                  <ProductPreviewCard
                    isLoading={isLoading}
                    {...i}
                    href={`${i.code}`}
                    isBgGray
                    key={i.id}
                    className={'bg-cLightGrey'}
                    variant={[0, 7].includes(index) ? 'large' : 'default'}
                    mobileVariant="catalog"
                  />
                </React.Fragment>
              ))
            : null}
          {/* {data?.pages.map((page) =>
            page?.products.map(
              (i: IProduct, index: number) => (
                <>
                  {index === 3 ? (
                    <AdvicesSlider></AdvicesSlider>
                  ) : null}
                  <ProductPreviewCard
                    {...i}
                    href={`${i.code}`}
                    key={i.id}
                    className={'bg-cLightGrey'}
                    variant={
                      [0, 8, 15].includes(index)
                        ? 'large'
                        : 'default'
                    }
                  />
                </>
              )
            )
          )} */}
          {isLoading ? <LoadingSpinner size={'md'} className={cvaProductsLoading()} /> : null}
        </div>
        <div
          className={cvaCatalogFooter({
            className: isLoading ? 'opacity-0' : '',
          })}
        >
          {/* {hasNextPage ? (
            <div className={cvaShowMore()}>
              <button
                type={'button'}
                className={cvaShowMoreButton()}
                onClick={() => fetchNextPage()}
              >
                Загрузить еще{' '}
                <IconPlus
                  className={cvaShowMoreButtonIcon()}
                />
              </button>
            </div>
          ) : null} */}
          {paginatedData?.page_data?.total_pages && paginatedData?.page_data?.total_pages > 1 ? (
            <>
              <Pagination
                pages={paginatedData.page_data.total_pages}
                currentPage={page}
                hrefBuilder={buildPageHref}
                onPageClick={onPageClick}
              />
            </>
          ) : null}
        </div>
      </Container>
      <div ref={mainRef}></div>
      {!paginatedData?.products?.length ? (
        <>
          {!searchQuery ? (
            <CatalogCategoryAboutSection
              mt={'none'}
              mb={'none'}
              title={'Подходящих товаров не найдено'}
              text={'Попробуйте изменить условия фильтрации<br> или выбрать другую категорию.'}
              buttonText="Сбросить фильтры"
              onButtonClick={() => {
                resetFilters();
                setFilterStateForQuery(getFilterStateBody());
              }}
            />
          ) : (
            <CatalogCategoryAboutSection
              mt={'none'}
              mb={'none'}
              title={'Ничего не нашли </br> по запросу'}
              text={`К сожалению, по вашему запросу </br> «${searchQuery}» ничего не найдено.`}
            />
          )}
          {recommendProducts?.length ? (
            <>
              <PageHeader pt={'none'} pb={'large'}>
                <PageTitle
                  text={'Рекомендуем'}
                  small={`${recommendProducts.length} ${wordEnding(recommendProducts.length, ['товар', 'товара', 'товаров'])}`}
                />
              </PageHeader>
              <Container variant={'large'}>
                <div className={cvaProductsList()}>
                  {recommendProducts.map((i: IProduct, index: number) => (
                    <React.Fragment key={i.id}>
                      <ProductPreviewCard
                        {...i}
                        href={`${i.code}`}
                        isBgGray
                        key={i.id}
                        className={'bg-cLightGrey'}
                        variant={[0, 8, 15].includes(index) ? 'large' : 'default'}
                      />
                    </React.Fragment>
                  ))}
                </div>
              </Container>
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

const cvaRoot = cva(['CatalogCategorySection-cvaRoot', 'mt-3.2 lg:mt-6.7']);

const cvaTotalCount = cva([
  'CatalogCategorySection-cvaTotalCount',
  'px-0.6 py-0.4 rounded-[0.8rem]',
  'text-bodyM text-cBlack64',
  'bg-cLightGrey',
]);

export const cvaProductsList = cva(
  [
    'CatalogCategorySection-cvaProductsList',
    'relative',
    'grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-0.8 sm:gap-1.2',
    '-mx-1.6 sm:mx-0 pt-7 md:pt-7',
    'before:absolute before:left-0 before:top-0 before:w-full before:h-full before:bg-cWhite before:opacity-0 before:[visibility:hidden] before:transition-all before:duration-300 before:z-[2] ',
  ],
  {
    variants: {
      isLoading: {
        true: ['h-[40rem] before:opacity-100 bg-cWhite/60 before:[visibility:visible]'],
        false: [''],
      },
    },
  }
);

const cvaProductsLoading = cva(['CatalogCategorySection-cvaProductsLoading', '!mt-0']);

export const cvaCatalogFooter = cva(['CatalogCategorySection-cvaCatalogFooter', 'pt-4.8']);

// const cvaShowMore = cva([
//   'CatalogCategorySection-cvaShowMore',
//   'mb-3.2',
//   'text-center',
// ]);

// const cvaShowMoreButton = cva([
//   'CatalogCategorySection-cvaShowMoreButton',
//   'inline-flex items-center justify-center gap-[0.45rem]',
//   'min-w-[24.7rem] sm:min-w-[34rem]',
//   'py-[1.8rem] sm:py-[2.45rem] pb-[2.2rem] sm:pb-[2.85rem] px-2',
//   'text-center text-bodyLSemibold',
//   'rounded-[3.2rem] border border-0.1 border-cBlack',
// ]);

export const cvaShowMoreButtonIcon = cva([
  'CatalogCategorySection-cvaShowMoreButtonIcon',
  'w-1.8 h-1.8',
  'relative top-0.2',
  'stroke-cBlack',
]);

const cvaFiltersRow = cva(['CatalogCategorySection-cvaFiltersRow', 'flex justify-between items-center gap-2.4']);

const cvaTogglersWrapper = cva(['CatalogCategorySection-cvaTogglersWrapper', 'relative overflow-hidden']);

const cvaTogglers = cva(['CatalogCategorySection-cvaTogglers', 'hidden md:flex flex-wrap gap-0.8', 'pt-3.2 pb-0.2']);

const cvaFiltersWrapper = cva(['CatalogCategorySection-cvaFiltersWrapper', 'relative', 'mt-1.1 lg:mt-1.6', 'z-[97]']);

const cvaFilters = cva(
  [
    'CatalogCategorySection-cvaFilters',
    'w-full',
    'left-0',
    'md:pt-0.9 pt-1.3 pb-1.2 md:py-1.6',
    'bg-cWhite',
    'md:border-t-[0.1rem] md:border-b-[0.1rem] md:rounded-bl-[8rem] md:rounded-br-[8rem] border-cBlack16 md:border-none',
  ],
  {
    variants: {
      fixed: {
        true: ['fixed z-[98]'],
        false: ['absolute'],
      },
    },
    defaultVariants: {
      fixed: false,
    },
  }
);
